import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { forwardRef } from 'react';
import CommerceTypeAvatar, {
  COMMERCE_TYPE_AVATAR_SIZE,
} from 'storybookConfig/stories/cells/Avatar/CommerceTypeAvatar';
import Body from 'storybookConfig/stories/molecules/Body';
import Icon from 'storybookConfig/stories/molecules/Icon';
import { CommerceType, type Company } from 'types/models/company';
import { isUserMembership, type UserMembership } from 'types/models/user';

const convertMembershipToCompanyPartial = (membership: UserMembership): Partial<Company> => ({
  commerceType: membership.commerceType,
  name: membership.companyName,
  url: membership.url,
});

const Wrapper = styled.button<Partial<CompanyProfileProps>>`
  all: unset;
  display: ${({ isFullWidth }) => (isFullWidth ? 'flex' : 'inline-flex')};
  gap: 8px;
  align-items: center;
  border-radius: ${COMMERCE_TYPE_AVATAR_SIZE};
  background: ${({ theme }) => theme.color.surfaceSubtle};
  padding-right: 16px;
  max-width: 100%;
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};

  /* \`all: unset\` drops the outline, so the button draws its own focus. */
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }

  ${({ variant, theme }) =>
    variant === 'compact' &&
    css`
      box-sizing: border-box;
      gap: 10px;
      padding: 8px 10px;
      border-radius: ${theme.radius.full};
    `}
`;

const TextWrapper = styled.div<Pick<CompanyProfileProps, 'variant'>>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: 0; // This is needed to allow truncation
  gap: ${({ variant }) => (variant === 'compact' ? '2px' : '0')};
`;

interface CompanyProfileProps {
  company: Partial<Company> | UserMembership | null;
  isFullWidth?: boolean;
  hasDropdownArrow?: boolean;
  /**
   * `compact` is the sidebar's company picker: a pill with a rounded-square avatar
   */
  variant?: 'default' | 'compact';
  onClick?: () => void;
}

const CompanyProfile = forwardRef<HTMLButtonElement, CompanyProfileProps>(
  (
    { company, isFullWidth, hasDropdownArrow, variant = 'default', onClick }: CompanyProfileProps,
    ref
  ) => {
    const data = isUserMembership(company) ? convertMembershipToCompanyPartial(company) : company;
    const commerceType = data?.commerceType || CommerceType.None;

    if (variant === 'compact') {
      return (
        <Wrapper
          ref={ref}
          type="button"
          isFullWidth={isFullWidth}
          variant={variant}
          onClick={onClick}
        >
          <CommerceTypeAvatar commerceType={commerceType} variant="compact" />

          <TextWrapper variant={variant}>
            <Body variant="Body/Body Small Bold" color="bodyTextPrimary" truncate>
              {data?.name}
            </Body>
            <Body variant="Chips/Chip Text Small" color="bodyTextCaption" truncate>
              {data?.url}
            </Body>
          </TextWrapper>

          {hasDropdownArrow && <Icon name="chevron-down" color="iconDefault" />}
        </Wrapper>
      );
    }

    return (
      <Wrapper ref={ref} type="button" isFullWidth={isFullWidth} onClick={onClick}>
        <CommerceTypeAvatar commerceType={commerceType} />

        <TextWrapper>
          <Body variant="Body/Body Small" truncate>
            {data?.name}
          </Body>
          <Body variant="Chips/Chip Text Small" truncate>
            {data?.url}
          </Body>
        </TextWrapper>

        {hasDropdownArrow && <Icon name="chevron-down" />}
      </Wrapper>
    );
  }
);

export default CompanyProfile;
